import { useState } from 'react';
import { Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GalleryLightbox } from '../components/GalleryLightbox';
import { galleryImages, beforeAfterImages, services } from '../data/mockData';
import { useNavigate } from 'react-router-dom';

const categories = ['Todos', 'Corte', 'Barba', 'Color', 'Facial'];
const collections = [
  { id: 'trending', name: 'Tendencias 2025', count: 8 },
  { id: 'classic', name: 'Estilos Clásicos', count: 6 },
  { id: 'bold', name: 'Looks Atrevidos', count: 5 }
];

export default function GalleryPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);

  const filteredImages = activeFilter === 'Todos'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeFilter);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleBookLook = (image) => {
    // Find related service based on category
    const relatedService = services.find(s => 
      s.category.toLowerCase().includes(image.category?.toLowerCase()) ||
      image.category?.toLowerCase().includes(s.category.toLowerCase().slice(0, -1))
    );
    
    setLightboxOpen(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-12" data-testid="gallery-page">
      {/* Header */}
      <section className="bg-cream py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl text-graphite mb-4">
            Galería
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Inspírate con nuestros trabajos. Encuentra tu próximo look y 
            reserva directamente.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-20 z-40 bg-white border-b border-border/50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Category filters */}
            <div className="flex gap-2 overflow-x-auto hide-scrollbar">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveFilter(category)}
                  data-testid={`gallery-filter-${category.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeFilter === category
                      ? 'bg-graphite text-cream'
                      : 'bg-soft-gray/50 hover:bg-soft-gray'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Before/After toggle */}
            <div className="flex items-center gap-4 md:ml-auto">
              <button
                onClick={() => setShowBeforeAfter(!showBeforeAfter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  showBeforeAfter
                    ? 'bg-copper text-cream'
                    : 'bg-soft-gray/50 hover:bg-soft-gray'
                }`}
                data-testid="toggle-before-after"
              >
                Antes / Después
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Collections */}
      {!showBeforeAfter && (
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-soft-gray/20">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl text-graphite mb-4">Colecciones</h2>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2">
              {collections.map((collection) => (
                <button
                  key={collection.id}
                  className="flex-shrink-0 px-6 py-4 bg-white rounded-xl border border-border/50 hover:border-copper/50 hover:shadow-premium transition-all text-left min-w-[180px]"
                  data-testid={`collection-${collection.id}`}
                >
                  <p className="font-medium">{collection.name}</p>
                  <p className="text-sm text-muted-foreground">{collection.count} looks</p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Gallery */}
      {!showBeforeAfter && (
        <section className="py-8 md:py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted-foreground text-sm">
                {filteredImages.length} imágenes
              </p>
            </div>

            <div className="gallery-grid">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="gallery-item cursor-pointer"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img src={image.url} alt={image.title} loading="lazy" />
                  <div className="gallery-item-overlay">
                    <div className="w-full">
                      <p className="text-white font-medium">{image.title}</p>
                      <p className="text-white/70 text-sm">{image.specialist}</p>
                      <span className="inline-block mt-2 px-2 py-0.5 bg-white/20 rounded text-xs text-white">
                        {image.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Before/After Gallery */}
      {showBeforeAfter && (
        <section className="py-8 md:py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl text-graphite mb-6">
              Transformaciones
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {beforeAfterImages.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl overflow-hidden border border-border/50 shadow-premium"
                  data-testid={`before-after-${item.id}`}
                >
                  <div className="grid grid-cols-2 gap-1">
                    <div className="relative aspect-square">
                      <img
                        src={item.before}
                        alt="Antes"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                        Antes
                      </span>
                    </div>
                    <div className="relative aspect-square">
                      <img
                        src={item.after}
                        alt="Después"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute bottom-2 left-2 px-2 py-1 bg-copper text-white text-xs rounded">
                        Después
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">{item.service}</p>
                        <p className="text-sm text-muted-foreground">Por {item.specialist}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate('/')}
                      className="w-full bg-graphite hover:bg-graphite/90 text-cream rounded-full text-sm"
                      data-testid={`book-look-${item.id}`}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Reservar este look
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-graphite">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
            ¿Te gustó lo que ves?
          </h2>
          <p className="text-cream/70 mb-8">
            Agenda tu cita y deja que nuestros especialistas te transformen.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-copper hover:bg-copper-dark text-cream px-8 py-3 rounded-full text-lg"
            data-testid="gallery-cta"
          >
            Agendar ahora <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Lightbox */}
      <GalleryLightbox
        images={filteredImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onBookService={handleBookLook}
      />
    </div>
  );
}
